import type { Issue, Landing, Proposal, Question, ReleaseMap, Session } from '../api/types'
import { stuckWhy } from './stuck'
import type { ReactNode } from 'react'
import { LIVE_FILTERS, type LiveFilter } from './view'
import { workingSessions } from './live'
import { readyProposals } from './ready'
import { readyToDeploy } from './deploy'
import { waitsOn } from '../sessions/attention'

// An approval's strip is over the board (ApprovalStrip); Needs you counts it,
// and a stopped batch, with the questions, as Code and the picker do.
export const SUMMARY_NAMES: Record<LiveFilter, string> = { working: 'Working', needs: 'Needs you', ready: 'Ready to merge', deploy: 'Ready to deploy', stuck: 'Stuck' }
export interface BoardSummary {
  counts: Record<LiveFilter, number>
  ids: Record<LiveFilter, ReadonlySet<string>>
}
/** Counts describe records, while filtering resolves those records to their issues. */
export function deriveSummary(project: string | null, issues: ReadonlyMap<string, Issue>, sessions: ReadonlyMap<string, Session>,
  questions: readonly Question[], landings: ReadonlyMap<string, Landing>, proposals: ReadonlyMap<string, Proposal>,
  returned: ReadonlyMap<string, Proposal> = new Map(), mainFailed = false, waiting: readonly Landing[] = [], releaseMap: ReleaseMap | null = null, personReviews = false): BoardSummary {
  const working = workingSessions(project, sessions)
  const needs = questions.filter(q => q.issue ? issues.get(q.issue)?.project === project : sessions.get(q.session)?.project_id === project)
  const asks = waiting.filter(l => l.project_id === project)
  const waitedOn = [...proposals.values()].filter(p => asks.some(l => waitsOn(l, p)))
  const ready = readyProposals(project, proposals, landings, personReviews)
  const ids = { working: new Set(working.flatMap(s => s.issue_id ? [s.issue_id] : [])), needs: new Set([...needs.flatMap(q => q.issue ? [q.issue] : []), ...waitedOn.map(p => p.issue_id)]),
    ready: new Set(ready.map(p => p.issue_id)), deploy: readyToDeploy(project, issues.values(), releaseMap) }
  // Main has no issue to filter: its failure stays above the board and
  // counts once in Stuck.
  // Work that needs you, is ready to merge or is at work is not stuck, as on today's board.
  const stuck = new Set([...issues.values()].filter(i => i.project === project && !i.archived_at && !ids.needs.has(i.id) && !ids.ready.has(i.id) && !ids.working.has(i.id)
    && stuckWhy(i, sessions.values(), proposals.values(), returned.values()) !== null).map(i => i.id))
  return {
    counts: { working: working.length, needs: needs.length + asks.length, ready: ready.length, deploy: ids.deploy.size, stuck: stuck.size + Number(mainFailed) },
    ids: { ...ids, stuck },
  }
}
export function Summary({ summary, filter, onFilter, newIssue, digest, active = true }: { summary: BoardSummary; filter: LiveFilter | null; onFilter: (filter: LiveFilter | null) => void; newIssue: ReactNode; digest?: ReactNode; active?: boolean }) {
  return <section className={active ? 'board-summary' : 'board-bar'} aria-label={active ? 'Board summary' : undefined}>
    <div className="summary-chips" role={active ? 'group' : undefined} aria-label={active ? 'Filter board' : undefined}>
      {active && LIVE_FILTERS.map(f => <button key={f} type="button" className="button summary-chip" aria-pressed={filter === f}
        aria-label={`${SUMMARY_NAMES[f]} ${summary.counts[f]}, filter board`} onClick={() => onFilter(filter === f ? null : f)}>
        {f === 'ready' && summary.counts.ready > 0 && <span className="summary-dot" aria-hidden="true" />}
        <span>{SUMMARY_NAMES[f]}</span><b>{summary.counts[f]}</b>
      </button>)}
      {newIssue}
    </div>
    {digest}
    {active && filter && <p className="visually-hidden" role="status">Showing only {SUMMARY_NAMES[filter]} issues.</p>}
  </section>
}
